'use client';

import { useMemo } from 'react';

import { AspectRatio, Box } from '@chakra-ui/react';

import { CUSTOM_IMAGE_SIZES } from '@nocowanie/core/consts';
import { CustomImageSize } from '@nocowanie/core/enums';
import { Image, ImageProps } from '@nocowanie/common-ui/components/atoms/image';

import { AccommodationTileImageProps } from '@app/components/molecules/accommodation-tile';
import { ACCOMMODATION_TILE } from '@app/consts';
import { MimeTypes } from '@app/enums';
import { useWpResizer } from '@app/hooks/useWpResizer';

export const AccommodationTileImage = ({
    accommodationName,
    withHighPriority = false,
    images,
    isOTA,
}: AccommodationTileImageProps) => {
    const breakpoint = ACCOMMODATION_TILE.LAYOUT_BREAKPOINT_EM;
    const wpresizer = useWpResizer();

    const desktopSize =
        CUSTOM_IMAGE_SIZES[
            isOTA
                ? CustomImageSize.AccommodationTileDesktopOTA
                : CustomImageSize.AccommodationTileDesktopInquiry
        ];

    const responsiveImages: (ImageProps | undefined)[] = useMemo(() => {
        const mobileSize = CUSTOM_IMAGE_SIZES[CustomImageSize.AccommodationTileMobile];

        return (images || []).slice(0, ACCOMMODATION_TILE.IMAGE_COUNT).map((image, index) => {
            const withPriority = withHighPriority && index === 0;
            const imageUrl = image?.url;
            const srcMobile = imageUrl
                ? wpresizer([mobileSize.width, mobileSize.height], imageUrl)
                : undefined;
            const srcDesktop = imageUrl
                ? wpresizer([desktopSize.width, desktopSize.height], imageUrl)
                : undefined;

            return imageUrl && srcMobile && srcDesktop
                ? {
                      src: srcMobile,
                      htmlWidth: mobileSize.width,
                      htmlHeight: mobileSize.height,
                      type: MimeTypes.webp,
                      loading: withPriority ? undefined : 'lazy',
                      fetchPriority: withPriority ? 'high' : undefined,
                      sources: [
                          {
                              srcSet: srcDesktop,
                              width: desktopSize.width,
                              height: desktopSize.height,
                              media: `(min-width: ${breakpoint})`,
                              type: MimeTypes.webp,
                          },
                      ],
                  }
                : undefined;
        });
    }, [breakpoint, images, withHighPriority, wpresizer]);

    const placeholderRatio = desktopSize.width / desktopSize.height;

    return (
        <>
            {responsiveImages?.length ? (
                responsiveImages.map((image, index) => (
                    <Image key={index} alt={accommodationName} {...image} />
                ))
            ) : (
                <AspectRatio
                    className={'accommodation-tile-image-placeholder'}
                    ratio={{
                        md: placeholderRatio,
                    }}
                >
                    <Box w={'full'} />
                </AspectRatio>
            )}
        </>
    );
};
